/**
 * CommandInterpreter — pure double-tap dash detector and attack-context resolver.
 *
 * No Phaser import — fully unit-testable in node. The Phaser adapter (Player.ts)
 * calls tickDash() each frame and resolveAttackContext() when an attack button
 * is pressed.
 *
 * Genre audit source: Streets of Rage 2/4, Double Dragon Neon, Final Fight,
 * Golden Axe, TMNT: Turtles in Time, Battletoads, Captain Commando,
 * Simpsons Arcade, Scott Pilgrim vs. the World (MY-2417).
 */

/**
 * Max frames between two same-direction taps that registers as a dash.
 * SOR4 ~14 frames; mirrored here.
 */
export const DASH_WINDOW_FRAMES = 14;

/** Frames a triggered dash-run lasts before it expires. */
export const DASH_DURATION_FRAMES = 36;

export interface DashState {
  /** True while the player is in a double-tap sprint. */
  active: boolean;
  /** Frames remaining in the current dash. Counts down to 0. */
  framesLeft: number;
  /** Direction of the current (or most recent) dash (-1 = left, 1 = right). */
  dir: 1 | -1;
  /** @internal direction of the most recent single tap (0 = none). */
  _lastTapDir: 1 | -1 | 0;
  /** @internal frame number when the last single tap was recorded. */
  _lastTapFrame: number;
}

export function createDashState(): DashState {
  return {
    active: false,
    framesLeft: 0,
    dir: 1,
    _lastTapDir: 0,
    _lastTapFrame: -9999,
  };
}

/**
 * Advance dash state one frame.
 *
 * Call once per game frame BEFORE handleAttacks so that the dash flag is
 * accurate when the attack context is resolved.
 *
 * @param state      Mutable DashState — mutated in-place.
 * @param leftJust   True on the exact frame LEFT was just pressed.
 * @param rightJust  True on the exact frame RIGHT was just pressed.
 * @param frame      Monotonically increasing game-frame counter.
 * @returns          True if a new dash started this frame.
 */
export function tickDash(
  state: DashState,
  leftJust: boolean,
  rightJust: boolean,
  frame: number,
): boolean {
  // Count down an active dash.
  if (state.active) {
    state.framesLeft -= 1;
    if (state.framesLeft <= 0) {
      state.active = false;
      state.framesLeft = 0;
    }
  }

  const tapDir: 1 | -1 | 0 = rightJust ? 1 : leftJust ? -1 : 0;
  if (tapDir === 0) return false;

  const sameTap = tapDir === state._lastTapDir;
  const withinWindow = frame - state._lastTapFrame <= DASH_WINDOW_FRAMES;

  if (sameTap && withinWindow && !state.active) {
    state.active = true;
    state.framesLeft = DASH_DURATION_FRAMES;
    state.dir = tapDir;
    state._lastTapDir = 0;
    state._lastTapFrame = -9999;
    return true;
  }

  state._lastTapDir = tapDir;
  state._lastTapFrame = frame;
  return false;
}

/**
 * Cancel an active dash (called on hit, throw, or attack landing).
 */
export function cancelDash(state: DashState): void {
  state.active = false;
  state.framesLeft = 0;
}

/**
 * True when the player is pressing the direction OPPOSITE to their facing —
 * the classic "back attack" present in SOR / Final Fight / TMNT.
 *
 * @param facing   Current facing direction (1 = right, -1 = left).
 * @param inputDx  Horizontal d-pad at attack moment (-1, 0, or 1).
 */
export function isBackAttack(facing: 1 | -1, inputDx: number): boolean {
  if (inputDx === 0) return false;
  return Math.sign(inputDx) !== facing;
}

/** Resolved movement-state context at the moment an attack button is pressed. */
export type AttackContext = 'normal' | 'jump' | 'dash' | 'back';

/**
 * Determine which attack variant applies given the player's current state.
 *
 * Priority (genre-consistent):
 *   1. airborne → jump attack (overrides all others)
 *   2. dash active → dash attack
 *   3. opposite direction held → back attack
 *   4. otherwise → normal ground attack
 *
 * @param airborne   True while the entity is off the ground plane.
 * @param dashActive True while a double-tap sprint is running.
 * @param facing     Current facing direction (1 = right, -1 = left).
 * @param inputDx    Horizontal d-pad input at attack moment (-1, 0, or 1).
 */
export function resolveAttackContext(
  airborne: boolean,
  dashActive: boolean,
  facing: 1 | -1,
  inputDx: number,
): AttackContext {
  if (airborne) return 'jump';
  if (dashActive) return 'dash';
  if (isBackAttack(facing, inputDx)) return 'back';
  return 'normal';
}
